import { useState, useEffect, memo } from 'react'
import { useI18n } from '../i18n/I18nContext'

interface GitHubTokenInputProps {
  onTokenSet: (token: string) => void | Promise<void>
  onTokenRemove?: () => void | Promise<void>
  currentToken?: string
  isLoading?: boolean
}

export const GitHubTokenInput = memo(function GitHubTokenInput({
  onTokenSet,
  onTokenRemove,
  currentToken,
  isLoading = false
}: GitHubTokenInputProps) {
  const { t } = useI18n()
  const [token, setToken] = useState('')
  const [isVisible, setIsVisible] = useState(false)
  const [isExpanded, setIsExpanded] = useState(!currentToken)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (currentToken) {
      setToken(currentToken.substring(0, 4) + '...' + currentToken.substring(currentToken.length - 4))
      setIsExpanded(false)
    }
  }, [currentToken])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!token.trim()) {
      setError(t('errors.token.required'))
      return
    }

    if (token.length < 20) {
      setError(t('errors.token.tooShort'))
      return
    }

    try {
      await onTokenSet(token.trim())
      setIsExpanded(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('errors.token.saveFailed')
      setError(msg)
    }
  }

  const handleClear = async () => {
    setToken('')
    setError(null)
    if (onTokenRemove) {
      await onTokenRemove()
    } else {
      await onTokenSet('')
    }
    setIsExpanded(true)
  }

  const maskedToken = currentToken
    ? currentToken.substring(0, 4) + '•'.repeat(20) + currentToken.substring(currentToken.length - 4)
    : ''

  return (
    <div className="github-token-container">
      <div className="token-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="token-header-content">
          <span className="token-icon">🔑</span>
          <div className="token-header-text">
            <strong>{t('token.header')}</strong>
            {currentToken ? (
              <span className="token-status active">
                <span className="status-dot"></span>
                {t('token.status.active')}
              </span>
            ) : (
              <span className="token-status inactive">
                <span className="status-dot"></span>
                {t('token.status.inactive')}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="token-toggle"
          aria-label={isExpanded ? t('token.toggle.collapse') : t('token.toggle.expand')}
        >
          {isExpanded ? '▲' : '▼'}
        </button>
      </div>

      {isExpanded && (
        <div className="token-form-container">
          <form onSubmit={handleSubmit} className="token-form">
            <div className="token-input-group">
              <label htmlFor="github-token" className="token-label">
                {t('token.label')}
              </label>
              <div className="token-input-wrapper">
                <input
                  id="github-token"
                  type={isVisible ? 'text' : 'password'}
                  value={currentToken ? maskedToken : token}
                  onChange={(e) => {
                    if (!currentToken) {
                      setToken(e.target.value)
                      setError(null)
                    }
                  }}
                  placeholder={t('token.placeholder')}
                  className="token-input"
                  disabled={!!currentToken || isLoading}
                  autoComplete="off"
                />
                {!currentToken && (
                  <button
                    type="button"
                    className="token-visibility-toggle"
                    onClick={() => setIsVisible(!isVisible)}
                    aria-label={isVisible ? t('token.visibility.hide') : t('token.visibility.show')}
                  >
                    {isVisible ? '👁️' : '👁️‍🗨️'}
                  </button>
                )}
              </div>
              {error && (
                <div className="token-error">
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              )}
              <div className="token-help">
                <p>
                  <strong>{t('token.help.title')}</strong>
                </p>
                <ol>
                  <li>{t('token.help.step1')}</li>
                  <li>{t('token.help.step2')}</li>
                  <li>{t('token.help.step3')}</li>
                  <li>{t('token.help.step4')}</li>
                </ol>
                <p className="token-warning">
                  {t('token.help.warning')}
                </p>
              </div>
            </div>

            <div className="token-actions">
              {currentToken ? (
                <>
                  <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="btn-secondary"
                    disabled={isLoading}
                  >
                    {t('token.close')}
                  </button>
                  <button
                    type="button"
                    onClick={handleClear}
                    className="btn-danger"
                    disabled={isLoading}
                  >
                    {t('token.remove')}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="btn-secondary"
                    disabled={isLoading}
                  >
                    {t('token.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isLoading || !token.trim()}
                  >
                    {isLoading ? t('token.validating') : t('token.submit')}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  )
})
